import React, { useState } from 'react';
import { Page } from 'react-pdf';
import { IconButton, SxProps } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { useAppSelector } from '../../hooks';
import { getSessionState } from '../../store/SessionSlice';
import { getAccessLimitState } from '../../store/AccessLimitSlice';
import QuestionPage from '../../pages/UserPages/Resources/components/QuestionPage';
import FeedbackPage from '../../pages/UserPages/Resources/components/FeedbackPage';
import PdfFirstPage from '../../pages/UserPages/Resources/components/PdfFirstPage';
import { isMobileScreen } from '../../helper';

interface PdfViewerBodyProps {
  selectedPageNumber: string;
  pageWidth?: number;
  scale: number;
  totalPages: number;
  onChangePage: (pageNo: string) => void;
  isResource: boolean;
  setActiveSection: (section: string) => void;
}

const PdfViewerBody: React.FC<PdfViewerBodyProps> = ({
  selectedPageNumber,
  pageWidth,
  scale,
  totalPages,
  onChangePage,
  isResource,
  setActiveSection,
}) => {
  // Store
  const sessionState = useAppSelector(getSessionState);
  const { premiumModalOpen, leadGenModalOpen } = useAppSelector(
    getAccessLimitState
  );

  const questions = sessionState.session?.questions;

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const isBackPageButtonDisabled = (Number(selectedPageNumber) || 1) < 2;
  const isNextPageButtonDisabled =
    (Number(selectedPageNumber) || 1) >= totalPages + (isResource ? 1 : 0);

  const isMobile = isMobileScreen();

  const navIconStyles: SxProps = {
    padding: '10px',
    zIndex: 2,
    opacity: 0.1,
    color: '#1E293B',
    transition: 'all 0.3s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: '#1E293B',
      color: 'white',
    },
  };

  if (isMobile) {
    navIconStyles.position = 'absolute';
    navIconStyles.color = '#1E293B';
    navIconStyles.backgroundColor = '#0000001A';
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;

    if (x < 100) {
      setShowLeftShadow(true);
      setShowRightShadow(false);
    } else if (x > rect.width - 100) {
      setShowLeftShadow(false);
      setShowRightShadow(true);
    } else {
      setShowLeftShadow(false);
      setShowRightShadow(false);
    }
  };

  const RENDER_CONTENT = () => {
    if (Number(selectedPageNumber) === 1) {
      return <PdfFirstPage />;
    }

    const questionPage = questions.find(
      (question) => question.pageNo === Number(selectedPageNumber) - 1
    );

    if (questionPage) {
      return <QuestionPage questionPage={questionPage} pageWidth={pageWidth} />;
    }

    if (totalPages + 1 === Number(selectedPageNumber)) {
      return (
        <FeedbackPage
          pageWidth={pageWidth}
          setActiveSection={setActiveSection}
        />
      );
    }

    return (
      <Page
        pageNumber={
          Number(selectedPageNumber) -
            1 - // Subtract 1 for PdfFirstPage
            questions.filter(
              (question) =>
                Number(question.pageNo) < Number(selectedPageNumber) - 1
            ).length || 1
        }
        scale={scale}
        width={pageWidth}
        className="pdf-rounded"
      />
    );
  };

  return (
    <div
      style={{
        maxWidth: pageWidth,
        opacity: leadGenModalOpen || premiumModalOpen ? '0.7' : '1',
        position: 'relative',
      }}
      onMouseMove={handleMouseMove}
    >
      {showLeftShadow && !isBackPageButtonDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '100px',
            background:
              'linear-gradient(to left, rgba(30, 41, 59, 0) 0%, rgba(30, 41, 59, 0.4) 100%)',
            zIndex: 1,
            cursor: 'pointer',
            borderTopLeftRadius: '15px',
            borderBottomLeftRadius: '15px',
          }}
          role="button"
          tabIndex={0}
          aria-label="Go to the previous page"
          onClick={() => {
            if (!isBackPageButtonDisabled) {
              onChangePage((Number(selectedPageNumber) - 1).toString());
            }
          }}
          onKeyDown={(e) => {
            if (
              !isBackPageButtonDisabled &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              onChangePage((Number(selectedPageNumber) - 1).toString());
            }
          }}
        />
      )}

      {showRightShadow && !isNextPageButtonDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '100px',
            background:
              'linear-gradient(to right, rgba(30, 41, 59, 0) 0%, rgba(30, 41, 59, 0.4) 100%)',
            zIndex: 1,
            cursor: 'pointer',
            borderTopRightRadius: '15px',
            borderBottomRightRadius: '15px',
          }}
          role="button"
          tabIndex={0}
          aria-label="Go to the next page"
          onClick={() => {
            if (!isNextPageButtonDisabled) {
              onChangePage((Number(selectedPageNumber) + 1).toString());
            }
          }}
          onKeyDown={(e) => {
            if (
              !isNextPageButtonDisabled &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              onChangePage((Number(selectedPageNumber) + 1).toString());
            }
          }}
        />
      )}

      <IconButton
        color="primary"
        sx={{
          ...navIconStyles,
          position: 'absolute',
          top: '50%',
          left: '10px',
          transform: 'translateY(-50%)',
          ...(showLeftShadow &&
            !isBackPageButtonDisabled && {
              opacity: 1,
              backgroundColor: '#1E293B',
              color: 'white',
            }),
        }}
        onClick={() =>
          onChangePage((Number(selectedPageNumber) - 1).toString())
        }
        disabled={isBackPageButtonDisabled}
      >
        <ArrowBackIcon fontSize={isMobile ? 'small' : 'medium'} />
      </IconButton>

      <IconButton
        color="primary"
        sx={{
          ...navIconStyles,
          position: 'absolute',
          top: '50%',
          right: '10px',
          transform: 'translateY(-50%)',
          ...(showRightShadow &&
            !isNextPageButtonDisabled && {
              opacity: 1,
              backgroundColor: '#1E293B',
              color: 'white',
            }),
        }}
        onClick={() =>
          onChangePage((Number(selectedPageNumber) + 1).toString())
        }
        disabled={isNextPageButtonDisabled}
      >
        <ArrowForwardIcon fontSize={isMobile ? 'small' : 'medium'} />
      </IconButton>

      {RENDER_CONTENT()}
    </div>
  );
};

export default PdfViewerBody;
